// Site chrome shared by every view: header, footer, static pages and an error boundary.
import { Component, type ComponentChildren } from 'preact';
import { t } from '../i18n';
import { focusSearch } from './Sections';

import { CONTACT } from './config';
const OPERATOR: string | undefined = (import.meta as any).env?.VITE_OPERATOR;

export function Logo({ size = 28 }: { size?: number }) {
  // sun on a sun-path arc above the horizon line
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true" class="logo">
      <path d="M4 24a12 12 0 0 1 24 0" fill="none" stroke="currentColor" stroke-width="1.6" stroke-dasharray="2.2 2.4" />
      <path d="M2 24.5h28" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
      <circle cx="21.5" cy="13.5" r="5" fill="var(--sun)" stroke="var(--sun-2)" stroke-width="1.4" />
    </svg>
  );
}

export function SiteHeader({ variant, onNew }: { variant: 'home' | 'app'; onNew?: () => void }) {
  const N = t.site.nav;
  return (
    <header class="topbar no-print">
      <a class="skip" href="#main">{t.site.skip}</a>
      <div class="topbar-in">
        <a class="brand" href="/" aria-label={`${t.appName} – ${N.home}`}><Logo /><span>{t.appName}</span></a>
        {variant === 'home' ? (
          <nav aria-label={N.label}>
            <a href="#primer">{N.demo}</a>
            <a href="#za-koga">{N.audience}</a>
            <a href="#kako-deluje">{N.how}</a>
            <a href="#vprasanja">{N.faq}</a>
            <a class="btn primary" href="#iskanje" onClick={focusSearch}>{N.start}</a>
          </nav>
        ) : (
          <nav aria-label={N.label}>
            {onNew ? <button type="button" class="btn" onClick={onNew}>{N.newParcel}</button> : <a class="btn" href="/">{N.home}</a>}
          </nav>
        )}
      </div>
    </header>
  );
}

export function SiteFooter({ extra }: { extra?: ComponentChildren }) {
  const F = t.site.footer;
  return (
    <footer class="sitefoot">
      <div class="sitefoot-in">
        <div class="sitefoot-brand">
          <a class="brand" href="/"><Logo size={22} /><span>{t.appName}</span></a>
          <p>{F.about}</p>
          <p>{F.coverage}</p>
        </div>
        <nav class="sitefoot-links" aria-label={t.appName}>
          <a href="/zasebnost">{F.privacy}</a>
          <a href={`mailto:${CONTACT}`}>{F.contact}: {CONTACT}</a>
        </nav>
        <p class="attr">{F.attribution}</p>
        {extra && <div class="foot">{extra}</div>}
      </div>
    </footer>
  );
}

export function Privacy() {
  const P = t.site.privacy;
  return (
    <>
      <SiteHeader variant="app" />
      <main id="main" class="wrap page">
        <header class="report-head">
          <span class="eyebrow">{t.appName}</span>
          <h1>{P.title}</h1>
          <p class="dataline">{P.updated}</p>
        </header>
        <div class="prose">
          {P.sections.map((s) => <section key={s.h}><h2>{s.h}</h2><p>{s.p}</p></section>)}
          {OPERATOR && <p>{P.operator(OPERATOR)}</p>}
          <p>{P.contact(CONTACT)}</p>
          <p><a href="/">{P.back}</a></p>
        </div>
      </main>
      <SiteFooter />
    </>
  );
}

export function NotFound() {
  const N = t.site.notFound;
  return (
    <>
      <SiteHeader variant="app" />
      <main id="main" class="wrap page">
        <header class="report-head">
          <span class="eyebrow">404</span>
          <h1>{N.title}</h1>
          <p class="lead">{N.lead}</p>
        </header>
        <p><a class="btn primary" href="/">{N.back}</a></p>
      </main>
      <SiteFooter />
    </>
  );
}

/** Last-resort boundary: a render error shows a reload prompt instead of a blank page. */
export class ErrorBoundary extends Component<{ children: ComponentChildren }, { failed: boolean }> {
  state = { failed: false };
  componentDidCatch(e: unknown) {
    console.error(e);
    this.setState({ failed: true });
  }
  render() {
    if (!this.state.failed) return this.props.children;
    const C = t.site.crash;
    return (
      <main id="main" class="wrap page">
        <div class="error sheet" role="alert">
          <h1>{C.title}</h1>
          <p>{C.lead}</p>
          <p><button class="btn primary" type="button" onClick={() => location.reload()}>{C.reload}</button> <a class="btn ghost" href="/">{t.site.nav.home}</a></p>
        </div>
      </main>
    );
  }
}
